import React, { useState, useEffect, useRef } from 'react';
import { initializePaddle, Paddle } from '@paddle/paddle-js';
import { CheckoutEventNames, PaddleEventData } from '@paddle/paddle-js';
import Button from './Button';
import LoadingSpinner from './LoadingSpinner';
import ErrorIcon from './icons/ErrorIcon';
import { useUser } from '@clerk/clerk-react';
import { toast, Toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

// These should be stored in your Paddle dashboard and environment variables
const PADDLE_PRICE_IDS = {
  '500': process.env.VITE_PADDLE_PRICE_ID_500, // 5 EUR for 500 credits
  '1200': process.env.VITE_PADDLE_PRICE_ID_1200, // 10 EUR for 1200 credits
  '3000': process.env.VITE_PADDLE_PRICE_ID_3000, // 20 EUR for 3000 credits
};

const PADDLE_ENVIRONMENT = process.env.VITE_PADDLE_ENVIRONMENT as 'production' | 'sandbox';
const PADDLE_CLIENT_TOKEN = process.env.VITE_PADDLE_CLIENT_TOKEN;

interface PaddleCheckoutViewProps {
  onCheckoutCompleted: (transactionId: string | null) => void;
}

const DismissableToast: React.FC<{ t: Toast; message: string }> = ({ t, message }) => {
  const { t: translate } = useTranslation();
  return (
    <div className="flex items-center justify-between w-full max-w-md">
      <span className="pr-4">{message}</span>
      <button
        onClick={() => toast.dismiss(t.id)}
        className="p-1 rounded-full hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400"
        aria-label={translate('paddleCheckoutView.dismiss')}
      >
        {/* Close Icon */}
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
      </button>
    </div>
  );
}

const PaddleCheckoutView: React.FC<PaddleCheckoutViewProps> = ({ onCheckoutCompleted }) => {
  const { t } = useTranslation();
  const [paddle, setPaddle] = useState<Paddle | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, isSignedIn, isLoaded } = useUser();
  // Use a ref to store the transaction ID to avoid re-renders.
  const completedTransactionId = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoaded) {
      return; // Wait for Clerk to load user data
    }

    if (!PADDLE_CLIENT_TOKEN || !PADDLE_ENVIRONMENT) {
      setError(t('paddleCheckoutView.configMissingError'));
      setIsLoading(false);
      return;
    }

    initializePaddle({
      environment: PADDLE_ENVIRONMENT,
      token: PADDLE_CLIENT_TOKEN,
      eventCallback: (event: PaddleEventData) => {
        switch (event.name) {
          case CheckoutEventNames.CHECKOUT_COMPLETED: {
            const transactionId = (event.data as any)?.transaction_id || null;
            if (!transactionId) {
              console.error("Checkout completed but no transaction ID found in Paddle event data.", event);
            }
            // Store transaction ID to be processed when the modal closes.
            // Use a special value if ID is missing but checkout was technically complete.
            completedTransactionId.current = transactionId || 'completed_without_id';
            
            break;
          }
          case CheckoutEventNames.CHECKOUT_CLOSED: {
            if (completedTransactionId.current) {
              const finalTransactionId = completedTransactionId.current === 'completed_without_id' ? null : completedTransactionId.current;
              onCheckoutCompleted(finalTransactionId);
              completedTransactionId.current = null; // Reset for next time
            }
            // No toast on cancellation as requested in the previous interaction.
            break;
          }
          case CheckoutEventNames.CHECKOUT_FAILED: {
            console.error('Checkout failed:', event.data);
            const message = t('paddleCheckoutView.checkoutFailed');
            toast.error((t) => <DismissableToast t={t} message={message} />, {
              duration: 6000,
              position: 'top-center',
            });
            break;
          }
          default:
            console.log('Unhandled Paddle event:', event.name, event.data);
        }
      }
    })
      .then((paddleInstance: Paddle | undefined) => {
        if (paddleInstance) {
          setPaddle(paddleInstance);
        } else {
          setError(t('paddleCheckoutView.initFailedError'));
        }
      })
      .catch((err) => {
        console.error("Paddle initialization error:", err);
        setError(t('paddleCheckoutView.loadProviderError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [isLoaded, onCheckoutCompleted, t]);

  const customerEmail = isSignedIn ? user?.primaryEmailAddress?.emailAddress : undefined;
  if (!customerEmail) {
    throw new Error(t('paddleCheckoutView.customerEmailError'));
  }

  let customerInfo = {
      email: customerEmail,
      address: {
        countryCode: "RS", 
      }
  }

  const openCheckout = (priceId: string) => {
    if (!paddle) {
      setError(t('paddleCheckoutView.providerNotReadyError'));
      return;
    }
    paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      customer: customerInfo,
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-300px)]">
        <LoadingSpinner />
        <p className="mt-4 text-lg text-slate-600">{t('paddleCheckoutView.loadingOptions')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center text-center p-6 min-h-[calc(100vh-300px)]">
        <ErrorIcon className="w-16 h-16 text-red-500 mb-4" />
        <h2 className="text-2xl font-semibold text-red-600 mb-2">{t('paddleCheckoutView.errorTitle')}</h2>
        <p className="text-slate-700 mb-6 max-w-md">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 md:p-8 shadow-xl rounded-lg">
      <h2 className="text-2xl md:text-3xl font-bold text-center text-sky-700 mb-8">{t('paddleCheckoutView.topUpCreditsTitle')}</h2>
      <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
        {t('paddleCheckoutView.selectPackage')}
      </p>
      <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
        <PricingCard
          title={t('paddleCheckoutView.pricingCard.title')}
          price="5€"
          credits={t('paddleCheckoutView.pricingCard.plan5.credits')}
          description={t('paddleCheckoutView.pricingCard.plan5.description')}
          buttonText={t('paddleCheckoutView.topUpButton')}
          onButtonClick={() => openCheckout(PADDLE_PRICE_IDS['500'])}
        />
        <PricingCard
          title={t('paddleCheckoutView.pricingCard.title')}
          price="10€"
          credits={t('paddleCheckoutView.pricingCard.plan10.credits')}
          description={t('paddleCheckoutView.pricingCard.plan10.description')}
          buttonText={t('paddleCheckoutView.topUpButton')}
          primary={true}
          onButtonClick={() => openCheckout(PADDLE_PRICE_IDS['1200'])}
        />
        <PricingCard
          title={t('paddleCheckoutView.pricingCard.title')}
          price="20€"
          credits={t('paddleCheckoutView.pricingCard.plan20.credits')}
          description={t('paddleCheckoutView.pricingCard.plan20.description')}
          buttonText={t('paddleCheckoutView.topUpButton')}
          onButtonClick={() => openCheckout(PADDLE_PRICE_IDS['3000'])}
        />
      </div>
    </div>
  );
};

interface PricingCardProps { title: string; price: string; credits: string; description: string; buttonText: string; primary?: boolean; onButtonClick?: () => void; }

const PricingCard: React.FC<PricingCardProps> = ({ title, price, credits, description, buttonText, primary = false, onButtonClick }) => {
  const { t } = useTranslation();
  const cardId = `pricing-card-${title.replace(/\s+/g, '-').toLowerCase()}`;
  const buttonAriaLabel = t('paddleCheckoutView.pricingCard.buttonAriaLabel', { buttonText, title });
  return (
    <div className={`border rounded-lg p-8 text-center flex flex-col transition-all duration-300 ${primary ? 'bg-sky-600 text-white border-sky-700 shadow-lg scale-105' : 'bg-white text-gray-800 hover:shadow-md hover:-translate-y-1'}`} aria-labelledby={cardId}>
      <h3 id={cardId} className="text-2xl font-bold">{title}</h3>
      <p className={`text-4xl font-extrabold my-4 ${primary ? 'text-white' : 'text-sky-600'}`}><span className="sr-only">{t('paddleCheckoutView.priceLabel')}</span>{price}</p>
      <p className="font-semibold">{credits}</p>
      <p className="text-sm mt-2 mb-6 flex-grow">{description}</p>
      <Button onClick={onButtonClick} size="lg" className={primary ? 'bg-white hover:bg-gray-100 font-bold transition duration-300 !text-sky-700 hover:!text-white' : 'bg-sky-600 text-white hover:bg-sky-700'} aria-label={buttonAriaLabel}>{buttonText}</Button>
    </div>
  );
};

export default PaddleCheckoutView;
